import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Truck, MapPin, Phone, Globe, Instagram, Mail, Loader2, ArrowLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierPublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/suppliers/${slug}`)
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <Truck className="w-16 h-16 text-stone-300 mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-stone-900 mb-2">Profil Bulunamadı</h1>
      <p className="text-stone-500 mb-6">Bu tedarikçi profili mevcut değil veya henüz yayınlanmamış.</p>
      <Link to="/tedarikciler"><Button variant="outline">Tüm Tedarikçiler</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Cover */}
      <div className="h-48 md:h-64 bg-gradient-to-r from-[#1B4F72] to-[#2E86AB] relative">
        {profile.cover_image && <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />}
        <Link to="/tedarikciler" className="absolute top-4 left-4 bg-white/90 backdrop-blur text-stone-700 px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 hover:bg-white">
          <ArrowLeft className="w-4 h-4" /> Tüm Tedarikçiler
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10 pb-12">
        {/* Profile Header */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-28 h-28 rounded-xl border-4 border-white bg-white overflow-hidden shadow-lg flex-shrink-0 -mt-16 md:-mt-20 flex items-center justify-center">
              {profile.logo_url ? (
                <img src={profile.logo_url} alt="" className="w-full h-full object-contain p-2" />
              ) : (
                <Truck className="w-12 h-12 text-stone-300" />
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-stone-900 font-serif">{profile.company_name}</h1>
              {profile.city && <p className="text-stone-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {profile.city}</p>}
              {(profile.categories?.length > 0 || profile.category) && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(profile.categories || [profile.category]).filter(Boolean).map((cat, i) => (
                    <Badge key={i} className="bg-[#1B4F72]/10 text-[#1B4F72] text-xs">{cat}</Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Phone className="w-4 h-4" /> {profile.phone}
                </a>
              )}
              {profile.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Mail className="w-4 h-4" /> {profile.email}
                </a>
              )}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Globe className="w-4 h-4" /> Website
                </a>
              )}
              {profile.instagram && (
                <a href={`https://instagram.com/${profile.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-stone-600 hover:text-[#1B4F72]">
                  <Instagram className="w-4 h-4" /> {profile.instagram}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {profile.description && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
            <h2 className="font-semibold text-stone-900 mb-3">Hakkımızda</h2>
            <p className="text-stone-600 whitespace-pre-line">{profile.description}</p>
          </div>
        )}

        {/* Services */}
        {profile.services?.length > 0 && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
            <h2 className="font-semibold text-stone-900 mb-3">Hizmetlerimiz</h2>
            <div className="flex flex-wrap gap-2">
              {profile.services.map((service, i) => (
                <Badge key={i} variant="outline" className="text-sm">{service}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Address */}
        {profile.address && (
          <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
            <h2 className="font-semibold text-stone-900 mb-3">Adres</h2>
            <p className="text-stone-600 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
              {profile.address}
            </p>
          </div>
        )}

        {/* Gallery */}
        {profile.gallery?.length > 0 && (
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="font-semibold text-stone-900 mb-4">Galeri</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {profile.gallery.map((img, i) => (
                <img key={i} src={img} alt="" className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
